<template>
    <div
        v-if="goal && goal.domain"
        class="widget h-100 d-flex flex-column justify-content-start align-items-start px-1 px-md-3 py-1 py-md-3 m-1"
    >
        <div class="w-100 d-flex justify-content-between align-items-center">
            <h6 :style="`color: ${goal.domain.color}`" class="font-weight-bold text-uppercase">
                {{ goal.domain.name }} goal:
            </h6>
        </div>

        <template v-if="!editing">
            <h5 class="mb-0">{{ goal.title }}</h5>
            <span class="d-flex align-items-center small text-muted mb-0">
                <em class="mr-2"
                    >goal created: {{ moment(goal.created_at).format("MMMM DD, YYYY") }}
                    {{ goal.created_by_user ? `by ${goal.created_by_user.name}` : "" }}</em
                >
                <profile-picture
                    v-if="goal.created_by_user"
                    :name="goal.created_by_user.name"
                    :size="'sm'"
                    :url="goal.created_by_user.profile_photo_url"
                ></profile-picture>
                <span>
                    <i
                        class="fa text-primary ml-2"
                        v-tooltip="goal.is_shared ? 'This is a shared goal' : null"
                        :class="goal.is_shared ? 'fa-users' : 'fa-fw'"
                    ></i>
                </span>
            </span>

            <hr style="width:100%;margin:auto;color:#c3c3c3;background: #c3c3c3; height: 1px;" />

            <p class="my-3">{{ goal.description }}</p>

            <template v-if="goal.tracker_type === 'time_sampling'">
                <p class="font-weight-bold font-weight-bold">Goal parameters</p>
                <p class="mb-0">
                    <span>Number of intervals: </span
                    ><span class="font-weight-bold">{{
                        helpers._get(goal, "parameters.intervals", "")
                    }}</span>
                </p>
                <p class="mb-0">
                    <span>Interval length: </span
                    ><span class="font-weight-bold">{{
                        helpers.secondsToMinutes(
                            helpers._get(goal, "parameters.interval_length", 0)
                        )
                    }}</span>
                </p>
            </template>

            <template v-if="goal.tracker_type === 'mand_echoic'">
                <div class="my-3">
                    <p class="mb-0">Targets:</p>
                    <div>
                        <small
                            class="badge-light p-1 m-1 rounded"
                            v-for="(target, index) in goal.parameters.parameters"
                            >{{ target }}
                        </small>
                    </div>
                </div>
            </template>

            <template v-if="['multi_percent', 'multi_tally'].includes(goal.tracker_type)">
                <div class="my-3">
                    <p class="mb-0">Targets:</p>
                    <div>
                        <small
                            class="badge-light p-1 m-1 rounded"
                            v-for="(target, index) in goal.parameters.targets"
                            >{{ target }}
                        </small>
                    </div>
                </div>
            </template>
        </template>

        <template v-else="">
            <input v-model="edited.title" class="mb-0" style="width:100%" />
            <small class="text-muted mb-0"
                ><em>goal created: {{ moment(goal.created_at).format("MMMM DD, YYYY") }}</em></small
            >
            <hr
                style="width:100%;margin:auto;color:#c3c3c3;background: #c3c3c3; height: 1px; margin-bottom: 1rem;"
            />
            <textarea class="mb-3" style="width:100%" v-model="edited.description" />

            <template
                v-if="
                    ['time_sampling', 'percent', 'mand', 'articulation', 'cme'].includes(
                        goal.tracker_type
                    )
                "
            >
                <div class="alert alert-info">
                    Additional parameters can be set in the Goals Overview page.
                </div>
            </template>

            <template v-if="goal.tracker_type === 'duration'">
                <div class="">
                    <em><span class="small "> Goal: </span></em>
                    <time-input v-model="goal.parameters.success"></time-input>
                </div>
            </template>

            <template v-if="goal.tracker_type === 'time_sampling'">
                <p class="font-weight-bold font-weight-bold">Goal parameters</p>
                <p class="mb-0">
                    <span>Number of intervals: </span
                    ><span class="font-weight-bold">{{
                        helpers._get(goal, "parameters.intervals", "")
                    }}</span>
                </p>
                <p class="mb-0">
                    <span>Interval length: </span
                    ><time-input v-model="edited.parameters.interval_length"></time-input>
                </p>
            </template>

            <template v-if="['tally', 'multi_tally'].includes(goal.tracker_type)">
                <div class="d-flex align-items-center">
                    <span class="mr-2">Goal: </span>
                    <input
                        v-model.number="goal.parameters.success"
                        name="success"
                        type="text"
                        class="d-inline form-control form-control-sm"
                        style="max-width: 2rem; line-height: 0.5;"
                    />
                    <span class="ml-2">times</span>
                </div>
                <div class="d-flex align-items-center">
                    <span class="mr-2"> Time limit (optional): </span>
                    <time-input v-model="goal.parameters.time_limit"></time-input>
                </div>
            </template>

            <template v-if="['multi_tally', 'multi_percent'].includes(goal.tracker_type)">
                <hr class="bar" />
                <div class="position-relative">
                    <p class="mb-0">Targets:</p>
                    <div class="mb-1">
                        <small class="text-muted">Add target: </small
                        ><input
                            type="text"
                            v-model="newMultiPercentTarget"
                            @keyup.enter="addMultiPercentTarget()"
                            name="target"
                            class="custom-input mx-2"
                        />
                        <a
                            class="btn btn-sm btn-outline-primary"
                            v-tooltip="'Add parameter'"
                            @click="addMultiPercentTarget()"
                            ><i class="text-primary fa fa-plus"></i
                        ></a>
                    </div>
                    <div>
                        <small
                            class="badge-light p-1 m-1 rounded"
                            v-for="target in edited.parameters.targets"
                            >{{ target
                            }}<i
                                class="fa fa-times-circle cursor-pointer ml-1"
                                @click="removeMultiPercentTarget(target)"
                            ></i>
                        </small>
                    </div>
                </div>
            </template>

            <template v-if="goal.tracker_type === 'mand_echoic'">
                <div>
                    <p class="mb-0">Targets:</p>
                    <div class="my-3 form-inline">
                        <small class="text-muted">Add target: </small
                        ><input
                            type="text"
                            v-model="newEchoicParameter"
                            @keyup.enter="addEchoicTarget()"
                            name="target"
                            class="form-control form-control-sm mx-3"
                        />
                        <a
                            class="btn btn-sm btn-outline-primary"
                            v-tooltip="'Add parameter'"
                            @click="addEchoicTarget()"
                            ><i class="text-primary fa fa-plus"></i
                        ></a>
                    </div>
                    <div class="mb-3">
                        <small
                            class="badge-light p-1 m-1 rounded"
                            v-for="(target, index) in edited.parameters.parameters"
                            >{{ target
                            }}<i
                                class="fa fa-times-circle cursor-pointer ml-1"
                                @click="removeEchoicTargetAtIndex(index)"
                            ></i>
                        </small>
                    </div>
                </div>
            </template>
        </template>

        <div class="w-100 d-flex justify-content-end align-items-center">
            <button
                v-if="userCanEditGoal && !editing"
                @click="toggleEdit()"
                class="text-uppercase font-weight-bold btn btn-sm btn-primary ml-3"
            >
                <i class="fa fa-edit mr-2"></i>Edit
            </button>
            <button v-if="editing" @click="toggleEdit()" class="text-dark btn btn-link ml-3">
                Cancel
            </button>
            <button
                v-if="editing"
                @click="updateGoal()"
                class="text-uppercase font-weight-bold btn btn-sm btn-primary ml-3"
            >
                <i class="fa fa-save mr-2"> </i>Save
            </button>
        </div>
    </div>
</template>

<script>
import { helpers } from "../../plugins/helpers";
import TimeInput from "../ui/TimeInput";
import ProfilePicture from "../ui/ProfilePicture";

export default {
    name: "Description",
    props: ["goal", "userCanEditGoal"],
    components: { TimeInput, ProfilePicture },
    data() {
        return {
            helpers,
            editing: null,
            saving: null,
            edited: {},
            newEchoicParameter: "",
            newMultiPercentTarget: "",
        };
    },
    methods: {
        toggleEdit() {
            if (this.editing) {
                this.editing = false;
                this.newEchoicParameter = "";
            } else {
                this.editing = true;
                this.edited = this.goal;
                this.newEchoicParameter = "";
            }
        },
        updateGoal() {
            axios
                .patch(`/api/patients/${this.goal.patient.id}/goals/${this.goal.id}`, this.edited)
                .then(response => {
                    this.$emit("refresh-goals");
                    this.editing = false;
                    this.edited = null;
                });
        },

        addEchoicTarget() {
            this.edited.parameters.parameters.push(this.newEchoicParameter);
            this.newEchoicParameter = "";
        },

        removeEchoicTargetAtIndex(index) {
            this.edited.parameters.parameters.splice(index, 1);
        },

        addMultiPercentTarget() {
            this.edited.parameters.targets.push(this.newMultiPercentTarget);
            this.newMultiPercentTarget = "";
        },
        removeMultiPercentTarget(target) {
            const index = this.edited.parameters.targets.indexOf(target);
            this.edited.parameters.targets.splice(index, 1);
        },
    },
};
</script>

<style scoped lang="scss">
.widget {
    background-color: #fff;
}
</style>
